// useHasBlockingOverlay.ts
// Copyright (c) 2026 BlueRock Security, Inc.
// All rights reserved.
//
// BLUEROCK SECURITY, INC. PROPRIETARY AND CONFIDENTIAL INFORMATION

// Detects whether an AntD Modal or Drawer is open anywhere in the document —
// watches for the mask element they render rather than any app-specific
// modal-tracking system, so this works for any AppShell consumer, not just
// one that happens to register modals through a particular tracking hook
// (corenet can't depend on a consumer app's own utilities).
//
// Deliberately keys off `.ant-modal-mask`/`.ant-drawer-mask`, NOT
// `.ant-modal-root`/`.ant-drawer`. AntD keeps a Modal's root mounted in the
// DOM after the first time it opens (only its `.ant-modal-wrap` toggles
// `display: none`), so a root-based check would latch permanently true the
// first time any Modal opens anywhere in the app and never go false again.
// The mask, by contrast, is actually unmounted/remounted on every
// close/open — confirmed live (not just via jsdom) by inspecting a real
// Modal's DOM before and after Cancel.
//
// The mask renders as a child of `.ant-modal-root`/`.ant-drawer`, which is
// itself a child of `document.body` — two levels deep — so this needs
// `subtree: true`, not just body's direct children.

import { useEffect, useState } from 'react';

const BLOCKING_OVERLAY_SELECTOR = '.ant-modal-mask, .ant-drawer-mask';

function hasBlockingOverlay(): boolean {
  return document.querySelector(BLOCKING_OVERLAY_SELECTOR) !== null;
}

export function useHasBlockingOverlay(): boolean {
  const [open, setOpen] = useState(hasBlockingOverlay);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setOpen(hasBlockingOverlay());
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return open;
}
